import { useState } from 'react'
import { useReactiveVar } from '@apollo/client'
import type { WalletName } from '@solana/wallet-adapter-base'
import { useWallet } from '@solana/wallet-adapter-react'
import {
  connectSelectedSolanaWallet,
  isUserRejectedWalletError
} from 'features/auth/utils/connect-solana-adapter'
import {
  PRIVY_WALLET_NAME,
  privyEmbeddedAddressesVar
} from 'features/auth-privy/store'
import { track } from 'shared/analytics/track'
import { userVar } from 'shared/store/user'
import { readWalletApp, withWalletChoice } from 'shared/store/wallet-choice'
import { notifyConnectWalletError, notifyOtherAccount } from '../utils/notify'

/**
 * Connects one of the user's linked wallets, which then plays: the XYRO
 * wallet through Privy's Wallet Standard adapter, an external one through the
 * wallet app that holds it on this device (`walletName` when the caller had
 * the user pick the app). The app becomes the user's choice, so neither the
 * Privy auto-selection nor wallet-adapter's autoConnect switches back, also
 * after a reload. Resolves to whether the wallet was selected or connected.
 */
export const useConnectLinkedWallet = () => {
  const { wallets, select } = useWallet()
  const user = useReactiveVar(userVar)
  const embeddedAddresses = useReactiveVar(privyEmbeddedAddressesVar)
  const [pendingAddress, setPendingAddress] = useState<string | null>(null)

  /** The wallet app that holds `address` here; null when not known yet. */
  const getWalletApp = (address: string) =>
    embeddedAddresses.includes(address) ? PRIVY_WALLET_NAME : (
      readWalletApp(address)
    )

  const connectWallet = async (
    address: string,
    walletName = getWalletApp(address)
  ) => {
    if (!user || !walletName || pendingAddress) return false

    track('wallet_connect_started', { wallet: walletName })
    setPendingAddress(address)
    try {
      await withWalletChoice(user.id, walletName, async () => {
        // NOTE: the provider connects a selected wallet in its own effect,
        // after it subscribed to the adapter's events (see the Privy effect)
        if (walletName === PRIVY_WALLET_NAME) {
          select(walletName as WalletName)
          return
        }

        const connected = await connectSelectedSolanaWallet(
          wallets,
          walletName,
          select
        )
        if (connected.walletAddress !== address) {
          notifyOtherAccount(walletName, address, connected.walletAddress)
        }
      })
      return true
    } catch (error) {
      if (!isUserRejectedWalletError(error)) notifyConnectWalletError(error)
      return false
    } finally {
      setPendingAddress(null)
    }
  }

  return { connectWallet, getWalletApp, pendingAddress }
}
